/**
 * Authentication Service
 * ---------------------
 * Handles M365 SSO via MSAL.js (Microsoft Authentication Library).
 * Used for web/phone users who authenticate directly in the browser.
 *
 * The kiosk does NOT authenticate users; it uses the API (Azure Functions)
 * with app-only credentials to read calendar availability.
 */
import * as msal from '@azure/msal-browser';

// These values come from your Azure AD app registration.
// Replace with your actual values before deployment.
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || 'YOUR_CLIENT_ID',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'YOUR_TENANT_ID'}`,
    // Always use the current origin so one build works across all office
    // domains (mke.ssp.team, hsv.ssp.team, hsv.3-gis.team, etc.). Hard-coding
    // a single domain here breaks popup login on every other domain.
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// Scopes needed for calendar operations
const LOGIN_SCOPES = ['User.Read'];
const CALENDAR_SCOPES = [
  'Calendars.ReadWrite',
  'Calendars.ReadWrite.Shared',
];

let msalInstance = null;

export const AuthService = {
  /**
   * Initialize MSAL and check for existing session.
   * Returns the current account if already signed in, null otherwise.
   */
  async initialize() {
    msalInstance = new msal.PublicClientApplication(msalConfig);
    await msalInstance.initialize();

    // Handle redirect response (if returning from login page)
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
      // Restore the original URL (with booking params) if we saved one before redirect
      const returnUrl = sessionStorage.getItem('auth_return_url');
      let restoredParams = null;
      if (returnUrl) {
        sessionStorage.removeItem('auth_return_url');
        const url = new URL(returnUrl);
        // Replace the browser URL so it looks right
        window.history.replaceState(null, '', url.pathname + url.search);
        // Parse the booking params so App.jsx can use them
        const sp = url.searchParams;
        if (sp.get('room')) {
          restoredParams = {
            roomId: sp.get('room'),
            date: sp.get('date'),
            startTime: sp.get('start'),
            endTime: sp.get('end'),
            subject: sp.get('subject') || '',
          };
        }
      }
      return {
        name: response.account.name,
        username: response.account.username,
        account: response.account,
        restoredBooking: restoredParams,
      };
    }

    // Check for existing session
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      return {
        name: accounts[0].name,
        username: accounts[0].username,
        account: accounts[0],
      };
    }
    return null;
  },

  /**
   * Trigger interactive login via popup (better UX than redirect for booking).
   */
  async login() {
    if (!msalInstance) throw new Error('Auth not initialized');

    try {
      const response = await msalInstance.loginPopup({
        scopes: [...LOGIN_SCOPES, ...CALENDAR_SCOPES],
      });
      msalInstance.setActiveAccount(response.account);
      return {
        name: response.account.name,
        username: response.account.username,
        account: response.account,
      };
    } catch (err) {
      // If popup is blocked, fall back to redirect, preserving current URL params (mode, room, etc.)
      if (err.errorCode === 'popup_window_error' || err.errorCode === 'empty_window_error') {
        // Store the current URL so we can return to it after auth redirect
        sessionStorage.setItem('auth_return_url', window.location.href);
        await msalInstance.loginRedirect({
          scopes: [...LOGIN_SCOPES, ...CALENDAR_SCOPES],
          redirectUri: window.location.origin,
        });
        return null; // Page will redirect
      }
      throw err;
    }
  },

  /**
   * Sign out the current user.
   */
  async logout() {
    if (!msalInstance) return;
    const account = msalInstance.getActiveAccount();
    if (account) {
      await msalInstance.logoutPopup({ account });
    }
  },

  /**
   * Get an access token for Microsoft Graph API calls.
   * Tries silent token acquisition first, falls back to interactive.
   */
  async getToken() {
    if (!msalInstance) throw new Error('Auth not initialized');

    const account = msalInstance.getActiveAccount();
    if (!account) throw new Error('No active account. Please sign in.');

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: CALENDAR_SCOPES,
        account,
      });
      return response.accessToken;
    } catch (err) {
      // Token expired or other silent failure; try interactive
      if (err instanceof msal.InteractionRequiredAuthError) {
        const response = await msalInstance.acquireTokenPopup({
          scopes: CALENDAR_SCOPES,
          account,
        });
        return response.accessToken;
      }
      throw err;
    }
  },
};
